import moment from "moment";
import { FieldErrors, FieldValues, useForm, Controller } from "react-hook-form";
import { ITeamDTO } from "../../../../../../api-services/football/admin/manage-football-team-service";
import { AppBox, AppInput, AppGridBox, AppText, AppDatePicker, AppButton } from "../../../../../../commons/components";
import { StatusEnum } from "../../../../../../commons/enums/status-enum";
import { isFormInvalid, findInputError } from "../../../../../../commons/utilities/form-utils";
import { toSentenceCase } from "../../../../../../commons/utilities/string-utils";
import { useAppDispatch, useAppSelector } from "../../../../../../hooks/app";
import { setStepperFormData } from "../../../../../../redux/slices/stepper-form-slice";
import { formatDate } from "../../../../../../commons/utilities/date-utils";
import { IFootballSubTeamDetail } from "../../../../../../commons/models/football/interface/i-football-sub-team";

interface SubTeamBasicInfoAddEditProps {
  subTeamData?: IFootballSubTeamDetail | null;
  formStep: number;
  formErrors: FieldErrors<FieldValues>;
  submitStatus: StatusEnum;
  onBack: (currentStep: number) => void;
  onNext: (currentStep: number) => void;
  onClearError: (errorKey: string) => void;
  onSaveAndExit: () => void;
}

const SubTeamBasicInfoAddEdit = ({
  subTeamData,
  formStep,
  formErrors,
  submitStatus,
  onSaveAndExit,
  onBack,
  onNext,
  onClearError,
}: SubTeamBasicInfoAddEditProps) => {
  const methods = useForm();
  const dispatch = useAppDispatch();
  const formData = useAppSelector<ITeamDTO>((state) => state.stepperForm.stepperFormData);

  const handleInputChange = (value: string, key: string) => {
    const teamData: ITeamDTO = {
      ...formData,
      [key]: value,
    };
    dispatch(setStepperFormData<ITeamDTO>(teamData));
    onClearError(key);
  };
  const handleDateChange = (value?: string) => {
    if (value) {
      const teamData: ITeamDTO = {
        ...formData,
        founded: formatDate(moment(value)),
      };
      dispatch(setStepperFormData<ITeamDTO>(teamData));
      onClearError("founded");
    }
  };
  return (
    <AppBox flexDirection="column" justifyContent="space-between" gap="sm" className="flex-1">
      <AppBox flexDirection="column" gap="sm" className="w-100">
        <Controller
          name="name"
          defaultValue={subTeamData ? subTeamData.name : ""}
          control={methods.control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <AppBox flexDirection="column">
              <AppInput
                id="name"
                label="Name *"
                placeholder="Enter your sub-team's name"
                type="text"
                defaultValue={value}
                onChange={(event) => {
                  onChange(event);
                  handleInputChange(event.target.value, "name");
                }}
                flex="row"
              />
              <AppGridBox style={{ gridTemplateColumns: "2fr 1fr", direction: "rtl" }} gap="sm">
                {isFormInvalid(findInputError(formErrors, "name")) && (
                  <AppText as="span" color="danger" textAlign="end">
                    <>{toSentenceCase(`${formErrors.name?.message ?? "Sub-team name is required."}`)}</>
                  </AppText>
                )}
              </AppGridBox>
            </AppBox>
          )}
        />
        <Controller
          name="shortName"
          defaultValue={subTeamData ? subTeamData.shortName : ""}
          control={methods.control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <AppBox flexDirection="column">
              <AppInput
                id="shortName"
                label="Short Name *"
                placeholder="Enter sub-team's short name, e.g. Brighton A"
                type="text"
                defaultValue={value}
                flex="row"
                onChange={(event) => {
                  onChange(event);
                  handleInputChange(event.target.value, "shortName");
                }}
              />
              <AppGridBox style={{ gridTemplateColumns: "2fr 1fr", direction: "rtl" }} gap="sm">
                {isFormInvalid(findInputError(formErrors, "shortName")) && (
                  <AppText as="span" color="danger" textAlign="end">
                    <>{toSentenceCase(`${formErrors.shortName?.message ?? "Sub-team's short name is required."}`)}</>
                  </AppText>
                )}
              </AppGridBox>
            </AppBox>
          )}
        />
        <Controller
          name="abbreviation"
          defaultValue={subTeamData ? subTeamData.abbreviation : ""}
          control={methods.control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <AppBox flexDirection="column">
              <AppInput
                id="abbreviation"
                label="Abbreviated Name *"
                placeholder="Enter sub-team's abbreviated name, e.g. BHA"
                type="text"
                defaultValue={value}
                flex="row"
                onChange={(event) => {
                  onChange(event);
                  handleInputChange(event.target.value, "abbreviation");
                }}
              />
              <AppGridBox style={{ gridTemplateColumns: "2fr 1fr", direction: "rtl" }} gap="sm">
                {isFormInvalid(findInputError(formErrors, "abbreviation")) && (
                  <AppText as="span" color="danger" textAlign="end">
                    <>{toSentenceCase(`${formErrors.abbreviation?.message ?? "Sub-team's abbreviation is required."}`)}</>
                  </AppText>
                )}
              </AppGridBox>
            </AppBox>
          )}
        />
        <Controller
          control={methods.control}
          name="founded"
          defaultValue={subTeamData?.founded ? moment(subTeamData.founded).toDate() : null}
          render={({ field: { onChange, value } }) => (
            <AppBox flexDirection="column" gap="2xs">
              <AppGridBox className="w-100 flex-1" style={{ gridTemplateColumns: "1fr 2fr" }} gap="sm">
                <AppBox alignItems="center">
                  <AppText as="label" size="lg">
                    <label>Foundation Date</label>
                  </AppText>
                </AppBox>
                <AppDatePicker
                  onChange={(event) => {
                    onChange(event);
                    handleDateChange(event?.toString());
                  }}
                  selected={value}
                  calenderPlacement="bottom-end"
                  maxDate={new Date()}
                />
              </AppGridBox>
              <AppGridBox style={{ gridTemplateColumns: "2fr 1fr", direction: "rtl" }} gap="sm">
                {isFormInvalid(findInputError(formErrors, "founded")) && (
                  <AppText as="span" color="danger" textAlign="end">
                    <>{toSentenceCase(`${formErrors.founded?.message}`)}</>
                  </AppText>
                )}
              </AppGridBox>
            </AppBox>
          )}
        />
        <Controller
          name="description"
          defaultValue={subTeamData ? subTeamData.description : ""}
          control={methods.control}
          rules={{ required: false }}
          render={({ field: { onChange, value } }) => (
            <AppBox flexDirection="column">
              <AppInput
                as="textarea"
                id="description"
                label="Description"
                placeholder="Enter description about your team"
                className="ee-input-textarea"
                defaultValue={value}
                flex="row"
                labelAlign="start"
                onChange={(event) => {
                  onChange(event);
                  handleInputChange(event.target.value, "description");
                }}
              />
              <AppGridBox style={{ gridTemplateColumns: "2fr 1fr", direction: "rtl" }} gap="sm">
                {isFormInvalid(findInputError(formErrors, "description")) && (
                  <AppText as="span" color="danger" textAlign="end">
                    <>{toSentenceCase(`${formErrors.description?.message}`)}</>
                  </AppText>
                )}
              </AppGridBox>
            </AppBox>
          )}
        />
      </AppBox>
      <AppBox gap="xs" justifyContent="end">
        <AppButton
          label="Back"
          variant="outline"
          color="gray"
          borderLight
          onClick={() => {
            onBack(formStep);
          }}
          disabled={submitStatus === StatusEnum.Loading}
        />
        {subTeamData && (
          <AppButton
            label="Save & Exit"
            variant="light"
            onClick={() => {
              onSaveAndExit();
            }}
            loading={submitStatus === StatusEnum.Loading}
          />
        )}
        <AppButton
          label="Next"
          onClick={() => {
            onNext(formStep);
          }}
          disabled={submitStatus === StatusEnum.Loading}
        />
      </AppBox>
    </AppBox>
  );
};

export default SubTeamBasicInfoAddEdit;
